/**
 * ControlEditModal — Full-featured control editing in a modal popup
 * 
 * Features:
 * - Edit all control fields (status, fulfillment, justification, owner, etc.)
 * - AI Compliance Verification that:
 *   - Reads attached evidence (documents, images, descriptions)
 *   - Analyzes against control requirements
 *   - Provides compliance assessment and recommendations
 * - Evidence management integration
 */

import React, { useState, useEffect } from 'react';
import {
  X, Save, Sparkles, Loader2, AlertTriangle, CheckCircle, Info,
  FileText, Paperclip, Eye, Shield, Clock, User, ChevronDown, ChevronUp,
  AlertCircle, Zap, RefreshCw, ExternalLink, BookOpen, FileQuestion, ListChecks, StickyNote
} from 'lucide-react';
import { GRCControl, Framework } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { useI18n } from '../../context/I18nContext';

interface ControlEditModalProps {
  control: GRCControl;
  framework: Framework;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedControl: GRCControl) => void;
  onOpenEvidence?: () => void;
}

interface AIVerificationResult {
  overall_status: 'compliant' | 'partial' | 'non_compliant' | 'needs_evidence';
  confidence: number;
  summary: string;
  findings: {
    type: 'pass' | 'warning' | 'fail' | 'info';
    message: string;
  }[];
  recommendations: string[];
  evidence_analysis: {
    document_name: string;
    relevance: 'high' | 'medium' | 'low';
    notes: string;
  }[];
  suggested_fulfillment_status: 'fulfilled' | 'partially_fulfilled' | 'not_fulfilled';
  suggested_compliance_status: 'compliant' | 'partial' | 'non_compliant';
}

export const ControlEditModal: React.FC<ControlEditModalProps> = ({
  control,
  framework,
  isOpen,
  onClose,
  onSave,
  onOpenEvidence
}) => {
  const { language } = useI18n();
  const t = language === 'sr' ? {
    title: 'Uređivanje kontrole',
    controlCode: 'Šifra kontrole',
    controlTitle: 'Naziv',
    description: 'Opis',
    status: 'Status usklađenosti',
    fulfillment: 'Status ispunjenosti',
    owner: 'Vlasnik',
    justification: 'Obrazloženje',
    implementationNotes: 'Napomene o implementaciji',
    applicable: 'Primenljivost',
    applicabilityJustification: 'Obrazloženje primenljivosti',
    save: 'Sačuvaj',
    cancel: 'Otkaži',
    aiVerify: 'AI Verifikacija',
    aiVerifying: 'Provera u toku...',
    verificationTitle: 'AI Analiza usklađenosti',
    confidence: 'Pouzdanost',
    findings: 'Nalazi',
    recommendations: 'Preporuke',
    evidenceAnalysis: 'Analiza dokaza',
    noEvidence: 'Nema priloženih dokaza. Dodajte dokaze za AI verifikaciju.',
    applyAiSuggestions: 'Primeni AI preporuke',
    manageEvidence: 'Upravljaj dokazima',
    evidenceCount: 'Priloženi dokazi',
    statusOptions: {
      not_assessed: 'Nije procenjeno',
      compliant: 'Usklađeno',
      partial: 'Delimično',
      non_compliant: 'Neusklađeno',
      not_applicable: 'Nije primenljivo'
    },
    fulfillmentOptions: {
      fulfilled: 'Ispunjeno',
      partially_fulfilled: 'Delimično ispunjeno',
      not_fulfilled: 'Nije ispunjeno'
    },
    applicableOptions: {
      yes: 'Da',
      no: 'Ne',
      partial: 'Delimično'
    },
    aiStatusLabels: {
      compliant: 'USKLAĐENO',
      partial: 'DELIMIČNO',
      non_compliant: 'NEUSKLAĐENO',
      needs_evidence: 'POTREBNI DOKAZI'
    }
  } : {
    title: 'Edit Control',
    controlCode: 'Control Code',
    controlTitle: 'Title',
    description: 'Description',
    status: 'Compliance Status',
    fulfillment: 'Fulfillment Status',
    owner: 'Owner',
    justification: 'Justification',
    implementationNotes: 'Implementation Notes',
    applicable: 'Applicability',
    applicabilityJustification: 'Applicability Justification',
    save: 'Save',
    cancel: 'Cancel',
    aiVerify: 'AI Verify',
    aiVerifying: 'Verifying...',
    verificationTitle: 'AI Compliance Analysis',
    confidence: 'Confidence',
    findings: 'Findings',
    recommendations: 'Recommendations',
    evidenceAnalysis: 'Evidence Analysis',
    noEvidence: 'No evidence attached. Add evidence for AI verification.',
    applyAiSuggestions: 'Apply AI Suggestions',
    manageEvidence: 'Manage Evidence',
    evidenceCount: 'Attached Evidence',
    statusOptions: {
      not_assessed: 'Not Assessed',
      compliant: 'Compliant',
      partial: 'Partial',
      non_compliant: 'Non-Compliant',
      not_applicable: 'Not Applicable'
    },
    fulfillmentOptions: {
      fulfilled: 'Fulfilled',
      partially_fulfilled: 'Partially Fulfilled',
      not_fulfilled: 'Not Fulfilled'
    },
    applicableOptions: {
      yes: 'Yes',
      no: 'No',
      partial: 'Partial'
    },
    aiStatusLabels: {
      compliant: 'COMPLIANT',
      partial: 'PARTIAL',
      non_compliant: 'NON-COMPLIANT',
      needs_evidence: 'NEEDS EVIDENCE'
    }
  };

  // Form state
  const [form, setForm] = useState({
    status: control.status || 'not_assessed',
    fulfillment_status: control.fulfillment_status || 'not_fulfilled',
    fulfillment_rationale: control.fulfillment_rationale || '',
    owner: control.owner || '',
    implementation_notes: control.implementation_notes || '',
    applicable: control.applicable ?? 1,
    applicability_justification: control.applicability_justification || ''
  });

  // AI verification state
  const [aiVerifying, setAiVerifying] = useState(false);
  const [aiResult, setAiResult] = useState<AIVerificationResult | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showAiDetails, setShowAiDetails] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDocumentDetails, setShowDocumentDetails] = useState(false);

  // Evidence state (fetched for display)
  const [evidence, setEvidence] = useState<any[]>([]);
  const [loadingEvidence, setLoadingEvidence] = useState(false);

  useEffect(() => {
    if (isOpen && control.id) {
      loadEvidence();
    }
  }, [isOpen, control.id]);

  const loadEvidence = async () => {
    setLoadingEvidence(true);
    try {
      const evidenceList = await grcService.getControlEvidence(control.id);
      setEvidence(evidenceList || []);
    } catch (err) {
      console.error('Failed to load evidence:', err);
    } finally {
      setLoadingEvidence(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await grcService.updateControl({ id: control.id, ...form });
      onSave({ ...control, ...form } as GRCControl);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  const runAiVerification = async () => {
    setAiVerifying(true);
    setAiError(null);
    setAiResult(null);
    
    try {
      const result = await grcService.verifyControlCompliance(control.id, framework.id);
      
      if (result.success) {
        setAiResult(result.verification);
      } else {
        setAiError(result.error || 'AI verification failed');
      }
    } catch (err: any) {
      setAiError(err.message || 'Failed to run AI verification');
    } finally {
      setAiVerifying(false);
    }
  };

  const applyAiSuggestions = () => {
    if (!aiResult) return;
    
    setForm(prev => ({
      ...prev,
      status: aiResult.suggested_compliance_status,
      fulfillment_status: aiResult.suggested_fulfillment_status,
      fulfillment_rationale: prev.fulfillment_rationale 
        ? `${prev.fulfillment_rationale}\n\n[AI Analysis]: ${aiResult.summary}`
        : `[AI Analysis]: ${aiResult.summary}`
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300';
      case 'partial': return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300';
      case 'non_compliant': return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300';
      case 'needs_evidence': return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getFindingIcon = (type: string) => {
    switch (type) {
      case 'pass': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'fail': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t.title}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {control.control_code || control.article_code} • {framework.name}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Control Info Header */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">{control.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">{control.description}</p>
            
            {/* Metadata badges */}
            <div className="flex flex-wrap gap-2 mt-3">
              {control.severity && (
                <span className={`px-2 py-0.5 text-xs rounded-full border ${
                  control.severity === 'critical' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300' :
                  control.severity === 'high' ? 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300' :
                  control.severity === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300' :
                  'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300'
                }`}>
                  {control.severity}
                </span>
              )}
              {control.check_type && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300">
                  {control.check_type}
                </span>
              )}
              {control.section_type && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300">
                  {control.section_type}
                </span>
              )}
            </div>
          </div>

          {/* Document Details Section - Collapsible */}
          {(control.testing_procedures || control.applicability_notes || control.completion_guidance || control.notes || control.gap_questions?.length > 0 || control.guidance) && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowDocumentDetails(!showDocumentDetails)}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <BookOpen className="w-4 h-4 text-indigo-500" />
                  {language === 'sr' ? 'Detalji iz dokumenta' : 'Document Details'}
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    ({language === 'sr' ? 'Testiranje, Uputstva, Napomene' : 'Testing, Guidance, Notes'})
                  </span>
                </span>
                {showDocumentDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showDocumentDetails && (
                <div className="p-4 space-y-4 bg-white dark:bg-gray-800">
                  {/* Expected Testing / Verification */}
                  {control.testing_procedures && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
                      <h5 className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-2">
                        <ListChecks className="w-4 h-4" />
                        {language === 'sr' ? 'Očekivano testiranje / Verifikacija' : 'Expected Testing / Verification'}
                      </h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{control.testing_procedures}</p>
                    </div>
                  )}

                  {/* SAQ Completion Guidance */}
                  {(control.completion_guidance || control.guidance) && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                      <h5 className="flex items-center gap-2 text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-2">
                        <BookOpen className="w-4 h-4" />
                        {language === 'sr' ? 'Uputstvo za ispunjavanje (SAQ Guidance)' : 'SAQ Completion Guidance'}
                      </h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{control.completion_guidance || control.guidance}</p>
                    </div>
                  )}

                  {/* Applicability Notes */}
                  {control.applicability_notes && (
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                      <h5 className="flex items-center gap-2 text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider mb-2">
                        <Info className="w-4 h-4" />
                        {language === 'sr' ? 'Napomene o primenljivosti' : 'Applicability Notes'}
                      </h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{control.applicability_notes}</p>
                    </div>
                  )}

                  {/* Gap Analysis Questions */}
                  {control.gap_questions && control.gap_questions.length > 0 && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                      <h5 className="flex items-center gap-2 text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wider mb-2">
                        <FileQuestion className="w-4 h-4" />
                        {language === 'sr' ? 'Pitanja za Gap analizu' : 'Gap Analysis Questions'}
                      </h5>
                      <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                        {(Array.isArray(control.gap_questions) ? control.gap_questions : [control.gap_questions]).map((q, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-orange-500">•</span>
                            <span>{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Notes */}
                  {control.notes && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                      <h5 className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                        <StickyNote className="w-4 h-4" />
                        {language === 'sr' ? 'Napomene' : 'Notes'}
                      </h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{control.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Form Fields */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                Status & Assessment
              </h4>

              {/* Compliance Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.status}
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                >
                  {Object.entries(t.statusOptions).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Fulfillment Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.fulfillment}
                </label>
                <select
                  value={form.fulfillment_status}
                  onChange={(e) => setForm({ ...form, fulfillment_status: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                >
                  {Object.entries(t.fulfillmentOptions).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Applicability */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.applicable}
                </label>
                <select
                  value={form.applicable}
                  onChange={(e) => setForm({ ...form, applicable: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={1}>{t.applicableOptions.yes}</option>
                  <option value={0}>{t.applicableOptions.no}</option>
                  <option value={2}>{t.applicableOptions.partial}</option>
                </select>
              </div>

              {/* Owner */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.owner}
                </label>
                <input
                  type="text"
                  value={form.owner}
                  onChange={(e) => setForm({ ...form, owner: e.target.value })}
                  placeholder="e.g., CISO, IT Manager"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Justification */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.justification}
                </label>
                <textarea
                  value={form.fulfillment_rationale}
                  onChange={(e) => setForm({ ...form, fulfillment_rationale: e.target.value })}
                  rows={3}
                  placeholder={language === 'sr' ? 'Opišite kako je kontrola ispunjena...' : 'Describe how this control is fulfilled...'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              {/* Applicability Justification */}
              {form.applicable !== 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.applicabilityJustification}
                  </label>
                  <textarea
                    value={form.applicability_justification}
                    onChange={(e) => setForm({ ...form, applicability_justification: e.target.value })}
                    rows={2}
                    placeholder={language === 'sr' ? 'Zašto kontrola nije primenljiva...' : 'Why this control is not applicable...'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>
              )}

              {/* Implementation Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.implementationNotes}
                </label>
                <textarea
                  value={form.implementation_notes}
                  onChange={(e) => setForm({ ...form, implementation_notes: e.target.value })}
                  rows={3}
                  placeholder={language === 'sr' ? 'Dodatne napomene o implementaciji...' : 'Additional implementation notes...'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
            </div>

            {/* Right Column - Evidence & AI Verification */}
            <div className="space-y-4">
              {/* Evidence Section */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    {t.evidenceCount}
                  </h4>
                  <span className="px-2 py-0.5 bg-emerald-200 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200 rounded-full text-xs font-medium">
                    {evidence.length}
                  </span>
                </div>

                {loadingEvidence ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                  </div>
                ) : evidence.length > 0 ? (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {evidence.map((ev, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg text-sm">
                        <FileText className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="truncate text-gray-700 dark:text-gray-300">{ev.file_name || ev.description || `Evidence ${idx + 1}`}</span>
                        <span className="ml-auto px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs">
                          {ev.evidence_type || 'document'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 italic">{t.noEvidence}</p>
                )}

                <button
                  onClick={onOpenEvidence}
                  className="mt-3 w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <Paperclip className="w-4 h-4" />
                  {t.manageEvidence}
                </button>
              </div>

              {/* AI Verification Section */}
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300 uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {t.verificationTitle}
                  </h4>
                  {aiResult && (
                    <button
                      onClick={() => setShowAiDetails(!showAiDetails)}
                      className="p-1 hover:bg-purple-200 dark:hover:bg-purple-800 rounded"
                    >
                      {showAiDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                </div>

                {/* AI Verify Button */}
                <button
                  onClick={runAiVerification}
                  disabled={aiVerifying}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-purple-400 disabled:to-indigo-400 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-500/20"
                >
                  {aiVerifying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.aiVerifying}
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      {t.aiVerify}
                    </>
                  )}
                </button>

                {/* AI Error */}
                {aiError && (
                  <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{aiError}</span>
                  </div>
                )}

                {/* AI Results */}
                {aiResult && showAiDetails && (
                  <div className="mt-4 space-y-4">
                    {/* Overall Status */}
                    <div className={`p-3 rounded-lg border ${getStatusColor(aiResult.overall_status)}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{t.aiStatusLabels[aiResult.overall_status]}</span>
                        <span className="text-sm">{t.confidence}: {Math.round(aiResult.confidence * 100)}%</span>
                      </div>
                      <p className="text-sm mt-1 opacity-90">{aiResult.summary}</p>
                    </div>

                    {/* Findings */}
                    {aiResult.findings.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase mb-2">{t.findings}</h5>
                        <div className="space-y-1">
                          {aiResult.findings.map((finding, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg text-sm">
                              {getFindingIcon(finding.type)}
                              <span className="text-gray-700 dark:text-gray-300">{finding.message}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {aiResult.recommendations.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase mb-2">{t.recommendations}</h5>
                        <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                          {aiResult.recommendations.map((rec, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-purple-500">•</span>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Evidence Analysis */}
                    {aiResult.evidence_analysis.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase mb-2">{t.evidenceAnalysis}</h5>
                        <div className="space-y-1">
                          {aiResult.evidence_analysis.map((ea, idx) => (
                            <div key={idx} className="p-2 bg-white dark:bg-gray-800 rounded-lg text-sm">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-800 dark:text-gray-200">{ea.document_name}</span>
                                <span className={`px-1.5 py-0.5 rounded text-xs ${
                                  ea.relevance === 'high' ? 'bg-green-100 text-green-700' :
                                  ea.relevance === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {ea.relevance}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{ea.notes}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Apply Suggestions Button */}
                    <button
                      onClick={applyAiSuggestions}
                      className="w-full px-4 py-2 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors border border-purple-200 dark:border-purple-800"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {t.applyAiSuggestions}
                    </button>
                  </div>
                )}
              </div>

              {/* Guidance Info (if available) */}
              {(control.guidance || control.implementation_method) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                  <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    {language === 'sr' ? 'Smjernice za implementaciju' : 'Implementation Guidance'}
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {control.guidance || control.implementation_method}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-lg shadow-indigo-500/20"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {language === 'sr' ? 'Čuvanje...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {t.save}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
